/* eslint-disable camelcase */
import { useAuth0 } from '@auth0/auth0-react'
import { requestGET, requestPOST } from '../services/request'
import { useQuery } from '@tanstack/react-query'
import { Device } from '../utils/models'

export interface IoTChartConfig {
  iot_path?: string
  path: string
  color?: string
  min_path?: string
  max_path?: string
  std_path?: string
  index?: string
  label?: string
  min_color?: string
  max_color?: string
  y_axis_label?: string
}

export interface IoTDeviceDataResponse {
  items: {
    timestamp: Date
    payload: any
  }[]
  metadata: {
    total_count: number
    count: number
    next_timestamp: Date
  }
}

export interface IotDeviceResponse {
  _id: string
  device_id: string
  device_name: string
  issue_ids: string[]
  description?: string
  cert_pem: string
  status: {
    last_message_seen?: Date
    total_messages: number
    is_online: boolean
  }
  chart_configs: IoTChartConfig[]
}

export const useDevices = (limit?: number) => {
  const auth = useAuth0()
  return useQuery<{
    items: Array<Device>
    metadata: { total_count: number; count: number; continuation_token: string }
  }>({
    queryKey: ['devices', limit],
    queryFn: async () => {
      const response = requestGET(
        limit ? `iot/devices?limit=${limit}` : `iot/devices`,
        undefined,
        auth,
      )
      return response
    },
  })
}

export const useDevice = (deviceId: string, organization?: string | null) => {
  const auth = useAuth0()
  return useQuery({
    queryKey: ['device', deviceId],
    queryFn: async (): Promise<IotDeviceResponse> => {
      const path = organization
        ? `iot/devices/${deviceId}?organization_name=${organization}`
        : `iot/devices/${deviceId}`
      console.log(organization)
      console.log(path)
      const response = requestGET(path, undefined, auth)
      return response
    },
  })
}

export const useDeviceData = ({
  deviceId,
  chartConfig,
  organizationName,
  limit = 1000,
  enabled = true,
  startTime,
  endTime,
}: {
  deviceId: string
  chartConfig?: IoTChartConfig
  organizationName?: string
  limit?: number
  enabled?: boolean
  startTime?: Date
  endTime?: Date
}) => {
  const auth = useAuth0()
  return useQuery({
    queryKey: [
      'devices-data',
      deviceId,
      chartConfig,
      limit,
      startTime,
      endTime,
    ],
    queryFn: async (): Promise<IoTDeviceDataResponse> => {
      const { iot_path, path, min_path, max_path, std_path } = chartConfig || {}

      const items: {
        timestamp: Date
        payload: any
      }[] = []
      let moreData = true
      let lastMetadata: any
      let newStartTime = startTime

      while (moreData) {
        const responsePage = await requestPOST(
          `iot/data`,
          {
            body: JSON.stringify({
              limit,
              device_id: deviceId,
              ...(organizationName
                ? { organization_name: organizationName }
                : {}),
              ...(chartConfig
                ? {
                    chart_projection: {
                      iot_path,
                      path,
                      min_path,
                      max_path,
                      std_path,
                    },
                  }
                : {}),
              ...(newStartTime
                ? { start_date: newStartTime.toISOString() }
                : {}),
              ...(endTime ? { end_date: endTime.toISOString() } : {}),
            }),
          },
          auth,
        )
        moreData = !!responsePage.metadata.next_timestamp
        lastMetadata = responsePage.metadata
        newStartTime = new Date(responsePage.metadata.next_timestamp)

        items.push.apply(items, responsePage.items)
      }

      return { items, metadata: lastMetadata }
    },
    enabled,
  })
}
